import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { CheckboxWithLabel, NeverAskCheckbox, SimpleDialog } from "../components";
import { sendCustomEvent } from "../events";
import { useDialog, useUniqueProjectID } from "../hooks";
import {
  Projection,
  selectDataProjection,
  updateDataProjection,
  updateViewProjection
} from "../redux/projections-slice";
import { useTranslation } from "react-i18next";
import { Grid, Pagination, Paper, Stack, Typography, useTheme } from "@mui/material";
import { resetAerialState, resetCameraState } from "../redux/camera-slice";
import { userProjections } from ".";
import path from "path";
import { PythonExecutable } from "../executable";

const eventKey = "las-projection";

interface ProjectionValues {
  assetPaths: string[];
  resolve: Function
}

interface ProjectionData {
  fileName: string;
  projection: Projection;
}

const getProjection = (assetPaths: string[]) : Promise<Projection[]> => {
  const exe = new PythonExecutable();

  return new Promise(resolve => {
    let projections: Projection[] = [];

    let commands = [
      "-p", "decode_projection",
      "--input_paths", assetPaths,
    ];

    exe.run({
      commands,
      saveLogs: true,
      onLine: jsonData => {
        if (!jsonData) return;
        projections = jsonData;
      },
      onClose: () => {
        resolve(projections);
      }
    });
  });
};

const ProjectionItem = (props: ProjectionData) => {
  const {fileName, projection} = props;

  const theme = useTheme();
  const {t} = useTranslation();

  return (
    <Paper
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        padding: theme.spacing(2),
        margin: theme.spacing(2, 0)
      }}
    >
      {projection && <React.Fragment>
        <Typography variant="subtitle2">
          <b>{t('projections.filename')}</b>
        </Typography>

        <Typography variant="caption" gutterBottom>
          {fileName}
        </Typography>

        <Typography variant="subtitle2">
          <b>{t('projections.projection-name')}</b>
        </Typography>

        <Typography variant="caption" gutterBottom>
          {projection.name}
        </Typography>

        <Typography variant="subtitle2">
          <b>{t('projections.projection_string')}</b>
        </Typography>

        <Typography variant="caption">
          {projection.string}
        </Typography>
      </React.Fragment>}
    </Paper>
  );
};

export const ProjectionsDetector = () => {
  const dialog = useDialog();
  const dispatch = useDispatch();
  const uniqueProjectID = useUniqueProjectID();
  const {t} = useTranslation();

  const [items, setItems] = useState<ProjectionData[]>([]);
  const [saveProjection, setSaveProjection] = useState(false);
  const [neverAsk, setNeverAsk] = useState(true);
  const [needsCheck, setNeedsCheck] = useState(true);
  const [page, setPage] = useState(1);

  const dataProjection = useSelector(selectDataProjection);

  /** LAS projection event listener */
  useEffect(() => {
    const callback = async (event) => {
      const {assetPaths, resolve} = event.detail as ProjectionValues;

      if (!dataProjection.default || !needsCheck) {
        resolve();
        return;
      }

      const fileNames = assetPaths.map(x => path.basename(x));
      const projections = await getProjection(assetPaths);

      const data = [] as ProjectionData[];

      for (let i=0; i<fileNames.length; i++) {
        const fileName = fileNames[i];
        const projection = projections[i];
        if (!projection) continue;

        data.push({
          fileName,
          projection: {
            ...projection,
            type: "proj4"
          }
        });


      }

      if (data.length === 0) {
        resolve();
        return;
      }

      setItems(data);

      dialog.handleOpen(resolve);
    };

    document.addEventListener(eventKey, callback);

    return () => {
      document.removeEventListener(eventKey, callback);
    };
  }, [dataProjection, needsCheck]);

  useEffect(() => {
    /** Reset when new project is loaded */
    dialog.handleClose();
    setNeverAsk(true);
    setNeedsCheck(true);
    setItems([]);
  }, [uniqueProjectID]);

  const handlePage = (event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
  };

  const onSubmit = async () => {
    dispatch(resetCameraState(null));
    dispatch(resetAerialState(null));

    if (existingProjection) {
      dispatch(updateDataProjection(existingProjection));
      dispatch(updateViewProjection(existingProjection));
    } else {
      if (saveProjection) {
        userProjections.save(uniqueProjection);
      }

      dispatch(updateDataProjection(uniqueProjection));
      dispatch(updateViewProjection(uniqueProjection));
    }

    dialog.handleClose();
    setNeedsCheck(false);

    const resolve = dialog.data;
    setTimeout(resolve, 500);
  };

  const onClose = async () => {
    dialog.handleClose();
    setNeedsCheck(!neverAsk);

    const resolve = dialog.data;
    setTimeout(resolve, 500);
  };

  const selected = items[page - 1];
  const hasMultiple = items.length > 1;

  const projection = selected
    ? selected.projection
    : null;

  const existingProjection = projection
    ? userProjections.getByString(projection.string)
    : null;

  const uniqueProjection = projection
    ? userProjections.withUniqueName(projection)
    : null;

  const useExisting = !!existingProjection;
  const createNew = !existingProjection;

  const buttonText = existingProjection
    ? t('projections.use_projection')
    : t('projections.import_projection');

  return (
    <SimpleDialog
      open={dialog.open}
      onClose={onClose}
      onSubmit={onSubmit}
      title={t('projections.projection_detected')}
      button={buttonText}
    >
      {useExisting && <Typography>
        {t('projections.las_projection_detected_existing')}
      </Typography>}

      {createNew && <Typography>
        {t('projections.las_projection_detected')}
      </Typography>}

      <Stack spacing={2}>
        <ProjectionItem {...selected}/>

        {hasMultiple && <Pagination
          count={items.length}
          page={page}
          onChange={handlePage}
          style={{
            width: "fit-content",
            margin: "0px auto"
          }}
        />}
      </Stack>

      {createNew && <Grid item xs={12} sx={{pt: 3}}>
        <CheckboxWithLabel
          title={t('projections.save_projection')}
          label={t('projections.the_new_projection_will_be_permanently_saved')}
          value={saveProjection}
          onChange={setSaveProjection}
        />
      </Grid>}

      <NeverAskCheckbox
        value={neverAsk}
        onChange={setNeverAsk}
      />

    </SimpleDialog>
  );
};

export const checkLASProjections = (assetPaths: string[]) : Promise<void> => {
  return new Promise(resolve => {
    sendCustomEvent(eventKey, {
      assetPaths: assetPaths,
      resolve: resolve
    });
  });
};
